<template>
  <div v-if="loading" class="flex items-center justify-center h-full">
    <loading-spinner diameter="64px"/>
  </div>

  <alert-badge
      v-else-if="error.title"
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else>
    <div class="flex flex-row">
      <div class="flex flex-col">
        <action-button :title="t('print')" faIcon="print" @click="print"/>
      </div>

      <div
          class="flex flex-col items-center mx-4 mt-3 px-2 relative overflow-hidden a4-size"
          v-if="settingsStore.getValue('examination_receipt_size') == 'a4'"
          id="examination-receipt"
          style="direction: ltr;overflow-x: hidden"
      >
        <img class="watermark"
             :src="settingsStore.getValue('examination_watermark_image') ? settingsStore.storagePath + '/' + settingsStore.getValue('examination_watermark_image'): watermarkImage"
             :style="{right: settingsStore.getValue('examination_watermark_position_right') + 'px', top: settingsStore.getValue('examination_watermark_position_top') + 'px', width: settingsStore.getValue('examination_watermark_size') + 'px'}"
             alt=""/>
        <img
            class="absolute"
            :src="settingsStore.getValue('examination_main_logo') ? settingsStore.storagePath + '/' + settingsStore.getValue('examination_main_logo'): samiLogo"
            :style="{left: settingsStore.getValue('examination_main_logo_position_left') + 'px', top: settingsStore.getValue('examination_main_logo_position_top') + 'px', width: settingsStore.getValue('examination_main_logo_size') + 'px'}"
            alt=""/>
        <div v-if="settingsStore.getValue('show_qr_codes_on_eye_examination_reports') == 'true'" class="examination-qr">
          <qrcode-vue
              :value="`https://exams.thecky.com/${settingsStore.getValue('viewer_client_key') || 'test'}/${$route.params.examination_id}`"
              :size="70"
              level="M"
              render-as="svg"
          />
        </div>
        <div class="flex flex-col items-center w-full" style="">
          <!-- header info -->
          <div id="examination-receipt-header" class="flex flex-col w-full">
            <div class="mt-6 w-full"></div>

            <div
                class="flex w-full justify-center items-center text-xl font-bold mt-[45px]">
              <span>Eye Examination Report - </span>
              <span class="mx-1">تقرير فحص البصر</span>
            </div>

            <div class="flex flex-col w-full items-center mt-5">
              <hr class="border-2 w-11/12" style="border-color: #585954"/>
              <hr class="border-2 w-7/12 mt-0.5" :style="{'border-color': settingsStore.getValue('receipt_branding_color')}"/>
            </div>
          </div>

          <div id="examination-receipt-body" class="w-full">
            <div
                class="flex items-center justify-between mt-4 flex-wrap gap-1 px-8">
              <!-- customer name -->
              <div class="flex items-center">
                <div class="font-bold">Name</div>
                <div
                    class="bg-gray-200 rounded py-1 px-2 mx-2 font-bold capitalize examination-input"
                >
                  {{ examination?.customer?.name ?? '' }}
                </div>
              </div>

              <!-- exam date -->
              <div class="flex items-center">
                <div class="font-bold">Exam Date</div>
                <div
                    class="bg-gray-200 rounded py-1 px-2 mx-2 font-bold capitalize examination-input"
                >
                  {{
                    `${formatPostgresDate(String(examination?.createdAt), 'DD-MM-YYYY')}`
                  }}
                </div>
              </div>

              <!-- exam no -->
              <div class="flex items-center">
                <div class="font-bold">Exam No.</div>
                <div
                    class="bg-gray-200 rounded py-1 px-2 mx-2 font-bold capitalize examination-input"
                >
                  {{ examination?.externalID ?? '' }}
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-col w-full items-center mt-5">
            <hr class="border-1 border-dashed w-11/12"
                style="border-color: #585954"/>
          </div>

          <div
              class="flex items-center justify-between mt-4 flex-wrap w-full px-8">
            <div class="flex flex-col">
              <div class="font-bold">VA Without Glasses</div>
              <div class="flex items-center mt-2">
                <!-- OD -->
                <div class="flex items-center">
                  <div class="font-bold">OD</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 mx-3 font-bold capitalize examination-input"
                  >
                    {{ examination?.vaWithoutGlassesOD ?? '' }}
                  </div>
                </div>

                <!-- OS -->
                <div class="flex items-center">
                  <div class="font-bold">OS</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 mx-3 font-bold capitalize examination-input"
                  >
                    {{ examination?.vaWithoutGlassesOS ?? '' }}
                  </div>
                </div>
              </div>
            </div>
            <div class="flex flex-col">
              <div class="font-bold">VA With Glasses</div>
              <div class="flex items-center mt-2">
                <!-- OD -->
                <div class="flex items-center">
                  <div class="font-bold">OD</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 mx-3 font-bold capitalize examination-input"
                  >
                    {{ examination?.vaWithGlassesOD ?? '' }}
                  </div>
                </div>

                <!-- OS -->
                <div class="flex items-center">
                  <div class="font-bold">OS</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 mx-3 font-bold capitalize examination-input"
                  >
                    {{ examination?.vaWithGlassesOS ?? '' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
              class="flex items-center justify-between mt-4 flex-wrap w-full px-8">
            <div class="flex flex-col">
              <div class="font-bold">I.O.P</div>
              <div class="flex items-center mt-2">
                <!-- OD -->
                <div class="flex items-center">
                  <div class="font-bold">OD</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 mx-3 font-bold capitalize examination-input"
                  >
                    {{
                      examination?.iopOD && !examination?.iopODStatus ? renderExaminationNumber(examination?.iopOD, true, true) : examination?.iopODStatus
                    }}
                  </div>
                </div>

                <!-- OS -->
                <div class="flex items-center">
                  <div class="font-bold">OS</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 mx-3 font-bold capitalize examination-input"
                  >
                    {{
                      examination?.iopOS && !examination?.iopOSStatus ? renderExaminationNumber(examination?.iopOS, true, true) : examination?.iopOSStatus
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-col w-full items-center mt-5">
            <hr class="border-1 border-dashed w-11/12"
                style="border-color: #585954"/>
          </div>

          <div class="examination-grid mt-4 w-full px-8">
            <!-- Distance Glasses Headers -->
            <div class="font-bold">Distance Glasses</div>
            <div class="font-bold"></div>
            <div class="font-bold"></div>
            <div class="font-bold mx-2">SPH</div>
            <div class="font-bold mx-2">CYL</div>
            <div class="font-bold mx-2">AXIS</div>
            <div class="font-bold mx-2">IPD</div>

            <!-- Distance Glasses OD -->
            <div class="font-bold"></div>
            <div class="font-bold"></div>
            <div class="font-bold">OD</div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.distanceODSPH) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.distanceODCYL) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{
                  renderExaminationNumber(examination?.distanceODAXIS, true, true)
                }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{
                  renderExaminationNumber(examination?.distanceODIPD, true, true)
                }}
              </div>
            </div>

            <!-- Distance Glasses OS -->
            <div class="font-bold"></div>
            <div class="font-bold"></div>
            <div class="font-bold">OS</div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.distanceOSSPH) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.distanceOSCYL) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{
                  renderExaminationNumber(examination?.distanceOSAXIS, true, true)
                }}
              </div>
            </div>
            <div class="mx-2"></div>
          </div>

          <div class="examination-grid mt-8 w-full px-8">
            <!-- Reading Glasses Headers -->
            <div class="font-bold">Reading Glasses</div>
            <div class="font-bold"></div>
            <div class="font-bold"></div>
            <div class="font-bold mx-2">SPH</div>
            <div class="font-bold mx-2">CYL</div>
            <div class="font-bold mx-2">AXIS</div>
            <div class="font-bold mx-2">IPD</div>

            <!-- Reading Glasses OD -->
            <div class="font-bold"></div>
            <div class="font-bold">
              <span v-if="examination?.readingAdd">Add</span>
            </div>
            <div class="font-bold">OD</div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.readingODSPH) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.readingODCYL) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{
                  renderExaminationNumber(examination?.readingODAXIS, true, true)
                }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{
                  renderExaminationNumber(examination?.readingODIPD, true, true)
                }}
              </div>
            </div>

            <!-- Reading Glasses OS -->
            <div class="font-bold"></div>
            <div class="font-bold">
              <span v-if="examination?.readingAdd">Add</span>
            </div>
            <div class="font-bold">OS</div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.readingOSSPH) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.readingOSCYL) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{
                  renderExaminationNumber(examination?.readingOSAXIS, true, true)
                }}
              </div>
            </div>
            <div class="mx-2"></div>
          </div>

          <div class="examination-grid mt-8 w-full px-8">
            <!-- Contact Lenses Headers -->
            <div class="font-bold">Contact Lenses</div>
            <div class="font-bold"></div>
            <div class="font-bold"></div>
            <div class="font-bold mx-2">SPH</div>
            <div class="font-bold mx-2">CYL</div>
            <div class="font-bold mx-2">AXIS</div>
            <div class="font-bold mx-2"></div>

            <!-- Contact Lenses OD -->
            <div class="font-bold"></div>
            <div class="font-bold"></div>
            <div class="font-bold">OD</div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.contactODSPH) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.contactODCYL) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{
                  renderExaminationNumber(examination?.contactODAXIS, true, true)
                }}
              </div>
            </div>
            <div class="mx-2"></div>

            <!-- Contact Lenses OS -->
            <div class="font-bold"></div>
            <div class="font-bold"></div>
            <div class="font-bold">OS</div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.contactOSSPH) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{ renderExaminationNumber(examination?.contactOSCYL) }}
              </div>
            </div>
            <div class="mx-2">
              <div
                  class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize examination-input-2"
              >
                {{
                  renderExaminationNumber(examination?.contactOSAXIS, true, true)
                }}
              </div>
            </div>
            <div class="mx-2"></div>
          </div>

          <div class="examination-grid mt-8 w-full px-8">
            <!-- Notes Headers -->
            <div class="font-bold"></div>
            <div class="font-bold"></div>
            <div class="font-bold"></div>
            <div class="font-bold mx-2">Notes</div>
            <div class="font-bold mx-2"></div>
            <div class="font-bold mx-2"></div>
            <div class="font-bold mx-2"></div>

            <div class="font-bold"></div>
            <div class="font-bold"></div>
            <div class="font-bold"></div>
            <div class="font-bold col-span-4 mx-2">
              <pre
                  class="font-bold bg-gray-200 rounded py-1 px-2 w-full custom-font"
                  style="min-height: 60px; direction: ltr; text-align: left"
              >{{ examination?.notes }}</pre
              >
            </div>
          </div>

          <div
              class="flex flex-col justify-center items-center self-center w-full mt-3">
            <div
                id="examination-receipt-footer"
                class="flex flex-col items-center justify-between pt-2 mt-2 mb-4 w-full"
            >
              <!-- hr -->
              <div class="flex flex-col w-full items-center mt-5">
                <hr class="border-1 border-dashed w-11/12"
                    style="border-color: #585954"/>
              </div>

              <div
                  class="flex items-center justify-between mt-4 flex-wrap w-full px-8">
                <!-- Optometrist -->
                <div class="flex items-center">
                  <div class="font-bold">Optometrist</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 mx-2 font-bold capitalize examination-input"
                  >
                    {{ examination?.examinator?.name ?? '' }}
                  </div>
                </div>

                <div v-if="examination?.doctor" class="flex items-center">
                  <div class="font-bold">Doctor</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 mx-2 font-bold capitalize examination-input"
                  >
                    {{ examination?.doctor ?? '' }}
                  </div>
                </div>
              </div>

              <div class="w-full mt-4">
                <div class="flex flex-col w-full items-center">
                  <hr class="border-1 border-solid w-11/12"
                      style="border-color: #585954"/>
                </div>
                <div class="flex w-full mt-3">
                  <div class="mx-8 pb-2 text-sm self-start">
                    <p>{{ settingsStore.getValue('company_email') }}</p>
                    <p>{{ settingsStore.getValue('company_website') }}</p>
                  </div>
                  <div class="mx-8 pb-2 text-sm ml-auto">
                    <p style="font-size: 12px">
                      {{ settingsStore.getValue('company_address') }}</p>
                    <p :class="{'ltr-numbers': settingsStore.getValue('receipt_language') == 'ar'}">{{
                        settingsStore.getValue('company_phone1') + (settingsStore.getValue('company_phone2') ? ` - ${settingsStore.getValue('company_phone2')}` : '')
                      }}</p>
                  </div>
                </div>
              </div>
              <div
                  v-if="settingsStore.getValue('show_receipts_thecky_footer') == 'true'"
                  class="flex items-center justify-between py-2 px-4 mt-2 mb-4"
                  style="bottom: 0px; width: 95%; border-top: 2px solid #BDC7D6"
              >
                <p><img src="@/assets/images/thecky-qr.png" style="height: 40px;"/></p>
          <p><img src="@/assets/images/footer.svg" style="height: 40px;"/></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
          class="flex flex-col items-center mx-4 mt-3 px-2 relative overflow-hidden a5-size text-xs"
          v-if="settingsStore.getValue('examination_receipt_size') == 'a5'"
          id="examination-receipt"
          style="direction: ltr; overflow-x: hidden"
      >
        <img class="watermark"
             :src="settingsStore.getValue('examination_watermark_image') ? settingsStore.storagePath + '/' + settingsStore.getValue('examination_watermark_image'): watermarkImage"
             :style="{right: settingsStore.getValue('examination_watermark_position_right') + 'px', top: settingsStore.getValue('examination_watermark_position_top') + 'px', width: settingsStore.getValue('examination_watermark_size') + 'px'}"
             alt=""/>
        <img
            class="absolute"
            :src="settingsStore.getValue('examination_main_logo') ? settingsStore.storagePath + '/' + settingsStore.getValue('examination_main_logo'): samiLogo"
            :style="{left: settingsStore.getValue('examination_main_logo_position_left') + 'px', top: settingsStore.getValue('examination_main_logo_position_top') + 'px', width: settingsStore.getValue('examination_main_logo_size') + 'px'}"
            alt=""/>
        <div v-if="settingsStore.getValue('show_qr_codes_on_eye_examination_reports') == 'true'" class="examination-qr">
          <qrcode-vue
              :value="`https://exams.thecky.com/${settingsStore.getValue('viewer_client_key') || 'test'}/${$route.params.examination_id}`"
              :size="70"
              level="M"
              render-as="svg"
          />
        </div>
        <div class="flex flex-col items-center w-full" style="">
          <!-- header info -->
          <div id="examination-receipt-header" class="flex flex-col w-full">
            <div class="mt-6 w-full"></div>

            <div
                class="flex w-full justify-center items-center text-lg font-bold mt-4">
              <span>Eye Examination Report - </span>
              <span class="mx-1">تقرير فحص البصر</span>
            </div>

            <div class="flex flex-col w-full items-center mt-1">
              <hr class="border-2 w-11/12" style="border-color: #585954"/>
              <hr class="border-2 w-7/12 mt-0.5" :style="{'border-color': settingsStore.getValue('receipt_branding_color')}"/>
            </div>
          </div>

          <div id="examination-receipt-body" class="w-full">
            <div
                class="grid grid-cols-3 mt-2 flex-wrap gap-4 px-8">
              <!-- customer name -->
              <div class="flex flex-col items-start">
                <div class="font-bold">Name</div>
                <div
                    class="bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[100px] min-h-[24px]"
                >
                  {{ examination?.customer?.name ?? '' }}
                </div>
              </div>

              <!-- exam date -->
              <div class="flex flex-col items-start">
                <div class="font-bold">Exam Date</div>
                <div
                    class="bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[100px] min-h-[24px]"
                >
                  {{
                    `${formatPostgresDate(String(examination?.createdAt), 'DD-MM-YYYY')}`
                  }}
                </div>
              </div>

              <!-- exam no -->
              <div class="flex flex-col items-start">
                <div class="font-bold">Exam No.</div>
                <div
                    class="bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[100px] min-h-[24px]"
                >
                  {{ examination?.externalID ?? '' }}
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-col w-full items-center mt-2">
            <hr class="border-1 border-dashed w-11/12"
                style="border-color: #585954"/>
          </div>

          <div
              class="grid grid-cols-2 justify-between mt-2 flex-wrap w-full px-8">
            <div class="flex flex-col">
              <div class="font-bold">VA Without Glasses</div>
              <div class="flex items-center gap-2 mt-2">
                <!-- OD -->
                <div class="flex flex-col items-start">
                  <div class="font-bold">OD</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[100px] min-h-[24px]"
                  >
                    {{ examination?.vaWithoutGlassesOD ?? '' }}
                  </div>
                </div>

                <!-- OS -->
                <div class="flex flex-col items-start">
                  <div class="font-bold">OS</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[100px] min-h-[24px]"
                  >
                    {{ examination?.vaWithoutGlassesOS ?? '' }}
                  </div>
                </div>
              </div>
            </div>
            <div class="flex flex-col">
              <div class="font-bold">VA With Glasses</div>
              <div class="flex items-center gap-2 mt-2">
                <!-- OD -->
                <div class="flex flex-col items-start">
                  <div class="font-bold">OD</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[100px] min-h-[24px]"
                  >
                    {{ examination?.vaWithGlassesOD ?? '' }}
                  </div>
                </div>

                <!-- OS -->
                <div class="flex flex-col items-start">
                  <div class="font-bold">OS</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[100px] min-h-[24px]"
                  >
                    {{ examination?.vaWithGlassesOS ?? '' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
              class="flex  mt-2  flex-wrap w-full px-8">
            <div class="flex w-full gap-2 items-center">
              <div class="font-bold">I.O.P</div>
              <div class="flex items-center gap-2">
                <!-- OD -->
                <div class="flex items-center gap-2">
                  <div class="font-bold">OD</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[100px] min-h-[24px]"
                  >
                    {{
                      examination?.iopOD && !examination?.iopODStatus ? renderExaminationNumber(examination?.iopOD, true, true) : examination?.iopODStatus
                    }}
                  </div>
                </div>

                <!-- OS -->
                <div class="flex items-center gap-2">
                  <div class="font-bold">OS</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[100px] min-h-[24px]"
                  >
                    {{
                      examination?.iopOS && !examination?.iopOSStatus ? renderExaminationNumber(examination?.iopOS, true, true) : examination?.iopOSStatus
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-col w-full items-center mt-5">
            <hr class="border-1 border-dashed w-11/12"
                style="border-color: #585954"/>
          </div>

          <div class="flex flex-col px-8">
           <div class="relative">
             <div class="font-bold absolute left-0 top-4">Distance Glasses</div>
             <div class="grid grid-cols-7 gap-y-1 mt-4 w-full">
               <!-- Distance Glasses Headers -->
               <div class="font-bold"></div>
               <div class="font-bold"></div>
               <div class="font-bold mx-2">SPH</div>
               <div class="font-bold mx-2">CYL</div>
               <div class="font-bold mx-2">AXIS</div>
               <div class="font-bold mx-2">IPD</div>

               <!-- Distance Glasses OD -->
               <div class="font-bold"></div>
               <div class="font-bold"></div>
               <div class="font-bold text-end pt-1">OD</div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{ renderExaminationNumber(examination?.distanceODSPH) }}
                 </div>
               </div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{ renderExaminationNumber(examination?.distanceODCYL) }}
                 </div>
               </div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{
                     renderExaminationNumber(examination?.distanceODAXIS, true, true)
                   }}
                 </div>
               </div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{
                     renderExaminationNumber(examination?.distanceODIPD, true, true)
                   }}
                 </div>
               </div>

               <!-- Distance Glasses OS -->
               <div class="font-bold"></div>
               <div class="font-bold"></div>
               <div class="font-bold text-end pt-1">OS</div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{ renderExaminationNumber(examination?.distanceOSSPH) }}
                 </div>
               </div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{ renderExaminationNumber(examination?.distanceOSCYL) }}
                 </div>
               </div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{
                     renderExaminationNumber(examination?.distanceOSAXIS, true, true)
                   }}
                 </div>
               </div>
               <div class="mx-2"></div>
             </div>
           </div>

            <div class="relative">
              <div class="font-bold absolute left-0 top-2">Reading Glasses</div>
              <div class="grid grid-cols-7 gap-y-1 mt-2 w-full">
                <!-- Reading Glasses Headers -->
                <div class="font-bold col-span-2"></div>
                <div class="font-bold mx-2">SPH</div>
                <div class="font-bold mx-2">CYL</div>
                <div class="font-bold mx-2">AXIS</div>
                <div class="font-bold mx-2">IPD</div>

                <!-- Reading Glasses OD -->
                <div class="font-bold"></div>
                <div class="font-bold text-end pt-1">
                  <span v-if="examination?.readingAdd">Add</span>
                </div>
                <div class="font-bold text-end pt-1">OD</div>
                <div class="mx-2">
                  <div
                      class="font-bold bg-gray-200 rounded py-1 px-2 capitalize min-w-[60px] min-h-[24px]"
                  >
                    {{ renderExaminationNumber(examination?.readingODSPH) }}
                  </div>
                </div>
                <div class="mx-2">
                  <div
                      class="font-bold bg-gray-200 rounded py-1 px-2 capitalize min-w-[60px] min-h-[24px]"
                  >
                    {{ renderExaminationNumber(examination?.readingODCYL) }}
                  </div>
                </div>
                <div class="mx-2">
                  <div
                      class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[60px] min-h-[24px]"
                  >
                    {{
                      renderExaminationNumber(examination?.readingODAXIS, true, true)
                    }}
                  </div>
                </div>
                <div class="mx-2">
                  <div
                      class="font-bold bg-gray-200 rounded py-1 px-2 capitalize min-w-[60px] min-h-[24px]"
                  >
                    {{
                      renderExaminationNumber(examination?.readingODIPD, true, true)
                    }}
                  </div>
                </div>

                <!-- Reading Glasses OS -->
                <div class="font-bold"></div>
                <div class="font-bold text-end pt-1">
                  <span v-if="examination?.readingAdd">Add</span>
                </div>
                <div class="font-bold text-end pt-1">OS</div>
                <div class="mx-2">
                  <div
                      class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[60px] min-h-[24px]"
                  >
                    {{ renderExaminationNumber(examination?.readingOSSPH) }}
                  </div>
                </div>
                <div class="mx-2">
                  <div
                      class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[60px] min-h-[24px]"
                  >
                    {{ renderExaminationNumber(examination?.readingOSCYL) }}
                  </div>
                </div>
                <div class="mx-2">
                  <div
                      class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[60px] min-h-[24px]"
                  >
                    {{
                      renderExaminationNumber(examination?.readingOSAXIS, true, true)
                    }}
                  </div>
                </div>
                <div class="mx-2"></div>
              </div>
            </div>

           <div class="relative">
             <div class="font-bold absolute left-0 top-2">Contact Lenses</div>
             <div class="grid grid-cols-7 gap-y-1 mt-2 w-full">
               <!-- Contact Lenses Headers -->
               <div class="font-bold"></div>
               <div class="font-bold"></div>
               <div class="font-bold mx-2">SPH</div>
               <div class="font-bold mx-2">CYL</div>
               <div class="font-bold mx-2">AXIS</div>
               <div class="font-bold mx-2"></div>

               <!-- Contact Lenses OD -->
               <div class="font-bold"></div>
               <div class="font-bold"></div>
               <div class="font-bold text-end pt-1">OD</div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{ renderExaminationNumber(examination?.contactODSPH) }}
                 </div>
               </div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{ renderExaminationNumber(examination?.contactODCYL) }}
                 </div>
               </div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{
                     renderExaminationNumber(examination?.contactODAXIS, true, true)
                   }}
                 </div>
               </div>
               <div class="mx-2"></div>

               <!-- Contact Lenses OS -->
               <div class="font-bold"></div>
               <div class="font-bold"></div>
               <div class="font-bold text-end pt-1">OS</div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{ renderExaminationNumber(examination?.contactOSSPH) }}
                 </div>
               </div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{ renderExaminationNumber(examination?.contactOSCYL) }}
                 </div>
               </div>
               <div class="mx-2">
                 <div
                     class="font-bold bg-gray-200 rounded py-1 px-2 font-bold capitalize min-w-[60px] min-h-[24px]"
                 >
                   {{
                     renderExaminationNumber(examination?.contactOSAXIS, true, true)
                   }}
                 </div>
               </div>
               <div class="mx-2"></div>
             </div>
           </div>

            <div class="flex flex-col gap-2 w-full">
              <div class="font-bold">Notes</div>
              <pre
                  class="font-bold bg-gray-200 rounded p-2 w-full custom-font col-span-2"
                  style="min-height: 60px; direction: ltr; text-align: left"
              >{{ examination?.notes }}</pre
              >
            </div>

          </div>



          <div
              class="flex flex-col justify-center items-center self-center w-full mt-3">
            <div
                id="examination-receipt-footer"
                class="flex flex-col items-center justify-between pt-2 mt-2 mb-4 w-full"
            >
              <!-- hr -->
              <div class="flex flex-col w-full items-center mt-1">
                <hr class="border-1 border-dashed w-11/12"
                    style="border-color: #585954"/>
              </div>

              <div
                  class="flex items-center justify-between mt-1 flex-wrap w-full px-8">
                <!-- Optometrist -->
                <div class="flex items-center">
                  <div class="font-bold">Optometrist</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 mx-2 font-bold capitalize min-w-[100px] min-h-[24px]"
                  >
                    {{ examination?.examinator?.name ?? '' }}
                  </div>
                </div>
                <div v-if="examination?.doctor" class="flex items-center">
                  <div class="font-bold">Doctor</div>
                  <div
                      class="bg-gray-200 rounded py-1 px-2 mx-2 font-bold capitalize min-w-[100px] min-h-[24px]"
                  >
                    {{ examination?.doctor ?? '' }}
                  </div>
                </div>
              </div>

              <div class="w-full mt-1">
                <div class="flex flex-col w-full items-center">
                  <hr class="border-1 border-solid w-11/12"
                      style="border-color: #585954"/>
                </div>
                <div class="flex w-full mt-1">
                  <div class="mx-8 pb-2 text-xs self-start">
                    <p>{{ settingsStore.getValue('company_email') }}</p>
                    <p>{{ settingsStore.getValue('company_website') }}</p>
                  </div>
                  <div class="mx-8 pb-2 text-xs ml-auto">
                    <p style="font-size: 12px">
                      {{ settingsStore.getValue('company_address') }}</p>
                    <p :class="{'ltr-numbers': settingsStore.getValue('receipt_language') == 'ar'}">{{
                        settingsStore.getValue('company_phone1') + (settingsStore.getValue('company_phone2') ? ` - ${settingsStore.getValue('company_phone2')}` : '')
                      }}</p>
                  </div>
                </div>
              </div>
              <div
                  v-if="settingsStore.getValue('show_receipts_thecky_footer') == 'true'"
                  class="flex items-center justify-between py-2 px-4 mt-2 mb-4"
                  style="bottom: 0px; width: 95%; border-top: 2px solid #BDC7D6"
              >
                <p><img src="@/assets/images/thecky-qr.png" style="height: 40px;"/></p>
          <p><img src="@/assets/images/footer.svg" style="height: 40px;"/></p>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {useI18n} from 'vue-i18n'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import Examination, {
  examinationI18nMessages
} from '@/models/customer/Examination'
import {formatPostgresDate, parseFormat} from '@/utils/date'
import ActionButton from '@/components/ActionButton.vue'
import {useSettingsStore} from '@/stores/settingsStore'

import samiLogo from '@/assets/images/logo.svg'
import watermarkImage from '@/assets/images/watermark.png'
import QrcodeVue from 'qrcode.vue'

export default defineComponent({
  components: {
    LoadingSpinner,
    AlertBadge,
    ActionButton,
    QrcodeVue
  },

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          ...examinationI18nMessages.en,
          print: 'Print'
        },
        ar: {
          ...examinationI18nMessages.ar,
          print: 'اطبع'
        }
      }
    })

    const settingsStore = useSettingsStore();

    function renderExaminationNumber(
        value: number | undefined,
        withoutMinus = false,
        withoutPrecision = false
    ): string {
      if (value == undefined) {
        return ''
      }

      if (withoutMinus && withoutPrecision) {
        return `${value}`
      }

      return value > 0 ? `+${value.toFixed(2)}` : `${value.toFixed(2)}`
    }

    return {
      t,
      parseFormat,
      formatPostgresDate,
      renderExaminationNumber,
      settingsStore,
      samiLogo,
      watermarkImage
    }
  },
  data() {
    return {
      originalTitle: document.title,
      examination: null as Examination | null,
      loading: true,
      error: {
        title: '',
        body: ''
      }
    }
  },
  async beforeMount() {
    if (this.$route.params.examination_id) {
      // get order
      try {
        const resp = await this.$http.get<Examination>(
            `${Examination.ENDPOINT}/${this.$route.params.examination_id}`
        )
        this.examination = Examination.from(resp)
        document.title = `Thecky ${this.examination.externalID} | ${formatPostgresDate(String(this.examination.createdAt), 'DD-MM-YYYY')}`
      } catch (error) {
        this.error.title = error.title
        this.error.body = error.body
      } finally {
        this.loading = false
      }
    }
  },
  created() {
    // Add print CSS when component is created
    this.addPrintCSS()
  },
  beforeUnmount() {
    this.removePrintCSS()
    document.title = this.originalTitle
  },
  methods: {
    print() {
      window.print()
    },
    addPrintCSS() {
      // Create a style element with a unique id
      const style = document.createElement('style')
      style.setAttribute('id', 'print-css')
      if (this.settingsStore.getValue('examination_receipt_size') == 'a4') {
        style.textContent = `
      @media print {
        @page {
          size: A4;
          margin: 0;
        }
        #examination-receipt-footer {
          bottom: 0;
          position: absolute;
        }
      }`
      } else if (this.settingsStore.getValue('examination_receipt_size') == 'a5') {
        style.textContent = `
      @media print {
        @page {
          size: A5;
          margin: 0;
        }
        #examination-receipt-footer {
          bottom: 0;
          position: absolute;
        }
      }`
      }

      // Append the style to the document head
      document.head.appendChild(style)
    },
    removePrintCSS() {
      // Find and remove the style element
      const style = document.querySelector('#print-css')
      if (style && style.parentNode) {
        style.parentNode.removeChild(style)
      }
    }
  }
})
</script>
<style>
.thecky-site {
  color: #000;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 3px;
}

.a4-size {
  width: 210mm !important;
  height: 297mm;
}

.a5-size {
  width: 148mm !important;
  height: 210mm;
}

#examination-receipt {
  overflow: scroll;
  overflow-x: hidden;
  background-color: #fff;
}

.examination-qr {
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

@media print {
  body * {
    visibility: hidden;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  #examination-receipt,
  #examination-receipt * {
    visibility: visible;
  }

  #examination-receipt {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    margin: 0cm;
  }

  #examination-receipt-header {
    top: 0;
  }

  #examination-receipt-body {
    position: relative;
  }

  p,
  div {
    page-break-inside: avoid;
  }
}

.watermark {
  position: absolute;
}

.examination-input {
  min-width: 100px;
  min-height: 32px;
}

.examination-grid {
  display: grid;
  grid-template-columns: 180px 40px 30px 100px 100px 100px 100px;
  row-gap: 8px;
}

.examination-input-2 {
  min-width: 60px;
  min-height: 32px;
}

.font-bold {
  font-weight: 500 !important;
}
</style>
